// src/hooks/useOrganizationForm.js
import { useState } from 'react';
import logger from '../utils/logger';

/**
 * Custom hook for organization form state and validation
 * Manages form data, validation rules, and duplicate checking
 */
const useOrganizationForm = (organizations = []) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    autoProvisionCredentials: false,
    receiveCredentialNotifications: false,
    enableDevStreamsForAllRoles: false,
  });

  // Default form values
  const getDefaultFormData = (forEdit = false) => ({
    name: "",
    autoProvisionCredentials: forEdit ? false : true,
    receiveCredentialNotifications: forEdit ? false : true,
    enableDevStreamsForAllRoles: false,
  });

  /**
   * Validate organization name (alphanumeric and spaces only, max 30 chars)
   */
  const validateOrganizationName = (name) => {
    const validNamePattern = /^[a-zA-Z0-9 ]+$/;
    return validNamePattern.test(name) && name.length <= 30;
  };

  /**
   * Check for duplicate organization names (case-insensitive)
   */
  const isDuplicateName = (name, excludeOrgId = null) => {
    return organizations.some(
      (org) =>
        org.name.toLowerCase() === name.toLowerCase() &&
        org.orgId !== excludeOrgId
    );
  };

  /**
   * Validate form and return error message if invalid
   */
  const validateForm = (name, excludeOrgId = null) => {
    // Validate name format
    if (!validateOrganizationName(name)) {
      if (name.length > 30) {
        return "Organization name must be 30 characters or less";
      } else {
        return "Organization name can only contain letters, numbers, and spaces";
      }
    }

    // Check for duplicates
    if (isDuplicateName(name, excludeOrgId)) {
      return "An organization with this name already exists. This validation is not case-sensitive.";
    }

    return null;
  };

  /**
   * Start creating a new organization
   */
  const startCreate = () => {
    logger.log('[useOrganizationForm] Starting create mode');
    setShowCreateForm(true);
    setEditingOrganization(null);
    setFormData(getDefaultFormData(false));
  };

  /**
   * Start editing an organization
   */
  const startEdit = (organization, settings = null) => {
    logger.log('[useOrganizationForm] Starting edit mode for:', organization.orgId);
    setShowCreateForm(false);
    setEditingOrganization(organization);
    
    if (settings) {
      setFormData({
        name: organization.name,
        ...settings,
      });
    } else {
      // When no settings, use default values but keep the organization name
      const defaults = getDefaultFormData(true);
      setFormData({
        ...defaults,
        name: organization.name,
      });
    }
  };

  /**
   * Cancel form and reset state
   */
  const cancelForm = () => {
    logger.log('[useOrganizationForm] Cancelling form');
    setShowCreateForm(false);
    setEditingOrganization(null);
    setFormData(getDefaultFormData(false));
  };

  /**
   * Reset form after successful submission
   */
  const resetForm = () => {
    setShowCreateForm(false);
    setEditingOrganization(null);
    setFormData(getDefaultFormData(false));
  };

  return {
    // State
    showCreateForm,
    editingOrganization,
    formData,
    
    // Setters
    setFormData,
    
    // Actions
    startCreate,
    startEdit,
    cancelForm,
    resetForm,
    
    // Validation
    validateForm,
  };
};

export default useOrganizationForm;
